import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, ClipboardList, MapPin, Phone, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, money } from "@/lib/api";

const STEPS = {
  scheduled: { label: "Scheduled", next: "out", action: "Out for delivery", cls: "bg-slate-100 text-slate-600" },
  out: { label: "Out for delivery", next: "delivered", action: "Mark delivered", cls: "bg-royal-light text-royal" },
  delivered: { label: "Delivered", next: "picked_up", action: "Mark picked up", cls: "bg-emerald-50 text-emerald-600" },
  picked_up: { label: "Picked up", next: null, action: null, cls: "bg-ink/5 text-slate-500" },
};

const WINDOW_LABELS = { morning: "9am–12pm", evening: "4pm–7pm", call: "call to discuss" };

const JobCard = ({ b, onAdvance, onPaid, busy }) => {
  const step = STEPS[b.fulfillment || "scheduled"] || STEPS.scheduled;
  const balanceDue = Math.round((b.balance_due || 0) * 100) / 100;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`delivery-card-${b.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs font-semibold text-royal">{b.reference}</span>
          <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${step.cls}`} data-testid={`fulfillment-badge-${b.id}`}>
            {step.label}
          </span>
        </div>
        <p className="mt-1.5 font-display text-base font-bold text-ink">{b.listing_title}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {fmtDate(b.start_date)} – {fmtDate(b.end_date)} · {money(b.total)}
        </p>
        {balanceDue > 0 ? (
          <p className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800" data-testid={`balance-due-${b.id}`}>
            <Banknote className="h-3.5 w-3.5" /> Collect {money(balanceDue)} on delivery
          </p>
        ) : (
          <p className="mt-1 inline-flex items-center rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid={`balance-due-${b.id}`}>
            Paid in full
          </p>
        )}
        <p className="mt-1 flex flex-wrap gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]" data-testid={`window-badges-${b.id}`}>
          <span className="rounded bg-royal-light px-2 py-0.5 text-royal">drop {WINDOW_LABELS[b.delivery_window] || "9am–12pm"}</span>
          <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-700">collect {WINDOW_LABELS[b.pickup_window] || "9am–12pm"}</span>
        </p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          <span className="font-semibold">{b.customer?.name}</span>
          {b.customer?.phone && (
            <a href={`tel:${b.customer.phone}`} className="flex items-center gap-1 text-royal hover:underline">
              <Phone className="h-3 w-3" /> {b.customer.phone}
            </a>
          )}
          {(b.customer?.address || b.delivery_zip) && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {b.customer?.address}{b.delivery_zip ? `, ${b.delivery_zip}` : ""}
              {b.self_pickup && <span className="ml-1 rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-amber-700">self pickup</span>}
            </span>
          )}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {balanceDue > 0 && (
          <button
            onClick={() => onPaid(b)}
            disabled={busy}
            data-testid={`paid-button-${b.id}`}
            className="flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-semibold text-white transition-all duration-200 hover:bg-emerald-700 disabled:opacity-50"
          >
            <Banknote className="h-3.5 w-3.5" /> Paid
          </button>
        )}
        {step.next && (
          <button
            onClick={() => onAdvance(b, step.next)}
            disabled={busy}
            data-testid={`fulfillment-button-${b.id}`}
            className="flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 text-xs font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
          >
            <Truck className="h-3.5 w-3.5" /> {step.action}
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, hint, items, children, testId }) => (
  <div className="mt-8" data-testid={testId}>
    <div className="flex items-baseline justify-between">
      <h3 className="font-display text-lg font-bold text-ink">{title}</h3>
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">{hint}</span>
    </div>
    <div className="mt-4 space-y-3">
      {items.length === 0 && (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
          Nothing here right now.
        </p>
      )}
      {items.map(children)}
    </div>
  </div>
);

export const DeliveriesTab = ({ bookings, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [routeDate, setRouteDate] = useState(today);
  const live = bookings.filter((b) => b.status === "confirmed");
  const stage = (b) => b.fulfillment || "scheduled";

  const pending = live
    .filter((b) => ["scheduled", "out"].includes(stage(b)) && b.end_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  const active = live
    .filter((b) => stage(b) === "delivered" && b.end_date >= today)
    .sort((a, b) => a.end_date.localeCompare(b.end_date));
  const pickupsDue = live.filter((b) => stage(b) === "delivered" && b.end_date < today);
  const returned = live
    .filter((b) => stage(b) === "picked_up")
    .sort((a, b) => b.end_date.localeCompare(a.end_date))
    .slice(0, 8);

  const advance = async (b, next) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${b.id}/fulfillment`, { status: next });
      toast.success(`${b.reference} → ${STEPS[next].label}`);
      onRefresh();
    } catch (e) {
      toast.error("Couldn't update that order — try again");
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (b) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/balance-paid`);
      toast.success(`${b.reference} — collected ${money(data.collected)} on delivery`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark that paid — try again");
    } finally {
      setBusy(false);
    }
  };

  const card = (b) => <JobCard key={b.id} b={b} onAdvance={advance} onPaid={markPaid} busy={busy} />;

  return (
    <div data-testid="deliveries-tab">
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="route-sheet-strip">
        <p className="text-xs font-semibold text-slate-600">
          Run sheet for the crew — route-ordered, tap-to-call, printable
        </p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={routeDate}
            onChange={(e) => setRouteDate(e.target.value)}
            data-testid="route-sheet-date"
            className="h-10 rounded-xl border border-slate-200 bg-paper px-3 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
          />
          <button
            onClick={() => navigate(`/admin/route?date=${routeDate}`)}
            data-testid="open-route-sheet-button"
            className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-semibold text-white transition-all duration-200 hover:bg-royal"
          >
            <ClipboardList className="h-3.5 w-3.5" /> Open route sheet
          </button>
        </div>
      </div>
      <Section title="Deliveries pending" hint="scheduled & out" items={pending} testId="deliveries-pending-section">{card}</Section>
      <Section title="Active rentals" hint="delivered, with customer" items={active} testId="deliveries-active-section">{card}</Section>
      <Section title="Pickups due" hint="window ended, not collected" items={pickupsDue} testId="deliveries-pickups-section">{card}</Section>
      <Section title="Returned" hint="last 8" items={returned} testId="deliveries-returned-section">{card}</Section>
    </div>
  );
};
